import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, Header, Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import BlockedPaymentMethodFilters from './BlockedPaymentMethodFilters';
import BlockedPaymentMethodModal from './BlockedPaymentMethodModal';
import BlockedPaymentMethodTable from './BlockedPaymentMethodTable';
import useBlockedPaymentMethod from './useBlockedPaymentMethod';

const BlockedPaymentMethodList = () => {
  const {
    isLoading,
    isSearch,
    fetchList,
    handleAddItem,
    handleSearch,
    handleRemoveItem,
    isPageReset,
    pageInfo,
    countryList,
    list,
    currentFilters,
    clearFilters,
    selectCurrentFilters,
  } = useBlockedPaymentMethod();

  const [showModal, setShowModal] = useState(false);

  const totalFigure = pageInfo?.total
    ? ` (${pageInfo.total.toLocaleString()})`
    : '';

  const loadedContent = list?.length ? (
    <BlockedPaymentMethodTable list={list} onRemove={handleRemoveItem} />
  ) : (
    <div className="flex items-center gap-1 justify-center mt-5">
      <p className="text-gray-800 dark:text-gray-200">
        No blocked payment methods found.
      </p>
    </div>
  );

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title={'Blocked Payment Method List' + totalFigure} />
      <PermissionsProvider
        permission={AfriexAdminPermission.BlockedPaymentMethodManage}
      >
        <div className="w-full flex justify-end my-3">
          <Button
            variant={'solid'}
            colorScheme="cyan"
            onClick={() => setShowModal(true)}
          >
            Add +
          </Button>
        </div>
      </PermissionsProvider>

      <BlockedPaymentMethodFilters
        currentFilters={currentFilters}
        clearFilters={clearFilters}
        selectCurrentFilters={selectCurrentFilters}
        handleSearch={handleSearch}
      />

      <div className="pt-5 py-10 overflow-x-auto">
        <section className="pt-5 md:pt-5 justify-center">
          {isLoading ? (
            <div className="min-w-full">
              <TableSkeleton />
            </div>
          ) : (
            loadedContent
          )}

          {list && list.length ? (
            <Pagination
              onPageChange={fetchList as PaginationProps['onPageChange']}
              total={pageInfo.total}
              limit={pageInfo.limit}
              isSearch={isSearch}
              isPageReset={isPageReset}
            />
          ) : null}
        </section>
      </div>

      <BlockedPaymentMethodModal
        showModal={showModal}
        countryList={countryList}
        onClose={() => setShowModal(!showModal)}
        handleSave={handleAddItem}
      />
    </main>
  );
};

export default BlockedPaymentMethodList;
